import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Pagination } from '@/components/Pagination';

interface ActivityLog {
  _id: string;
  userId: string;
  email: string;
  activityType: string;
  platform: string;
  ipAddress: string;
  browser?: { name: string; version: string; vendor?: string; model?: string };
  os?: { name: string; version: string };
  device?: { type: string; vendor?: string; model?: string };
  geolocation?: { city: string; country: string; region?: string; timezone?: string; ll?: [number, number] };
  success: boolean;
  errorMessage?: string;
  userAgent?: string;
  timestamp: string;
}

export default function ActivityLogs() {
  const qc = useQueryClient();
  const [activityType, setActivityType] = useState('all');
  const [searchEmail, setSearchEmail] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);

  const { data, isLoading: loading } = useQuery({
    queryKey: ['/api/admin/activity-logs', activityType, searchEmail, page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams({
        skip: ((page - 1) * pageSize).toString(),
        limit: pageSize.toString(),
      });
      if (activityType && activityType !== 'all') params.append('activityType', activityType);
      if (searchEmail) params.append('searchEmail', searchEmail);
      return apiRequest('GET', `/api/admin/activity-logs?${params}`) as Promise<{ logs: ActivityLog[]; total: number }>;
    },
  });

  const logs: ActivityLog[] = (data as any)?.logs ?? [];
  const total: number = (data as any)?.total ?? 0;

  const getActivityBadge = (type: string, success: boolean) => {
    if (!success) return <Badge variant="destructive">{type}</Badge>;

    const colors: Record<string, string> = {
      login: 'default',
      logout: 'secondary',
      signup: 'default',
      failed_login: 'destructive',
      password_reset: 'secondary',
      recipe_created: 'default',
      recipe_updated: 'secondary',
      recipe_deleted: 'destructive',
      analytics_viewed: 'secondary',
      dashboard_viewed: 'secondary',
      order_created: 'default',
      order_updated: 'secondary',
      order_deleted: 'destructive',
      order_payment_recorded: 'default',
      order_payment_deleted: 'destructive',
      customer_created: 'default',
      customer_updated: 'secondary',
      customer_deleted: 'destructive',
    };

    return <Badge variant={colors[type] as any || 'default'}>{type}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-3xl font-bold">Activity Logs</h1>
        <p className="text-muted-foreground">Monitor user activity and system events</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="flex gap-4">
          <Select value={activityType} onValueChange={setActivityType}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Activities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Activities</SelectItem>
              <SelectItem value="login">Login</SelectItem>
              <SelectItem value="logout">Logout</SelectItem>
              <SelectItem value="signup">Signup</SelectItem>
              <SelectItem value="failed_login">Failed Login</SelectItem>
              <SelectItem value="password_reset">Password Reset</SelectItem>
              <SelectItem value="recipe_created">Recipe Created</SelectItem>
              <SelectItem value="recipe_updated">Recipe Updated</SelectItem>
              <SelectItem value="recipe_deleted">Recipe Deleted</SelectItem>
              <SelectItem value="recipe_viewed">Recipe Viewed</SelectItem>
              <SelectItem value="analytics_viewed">Analytics Viewed</SelectItem>
              <SelectItem value="dashboard_viewed">Dashboard Viewed</SelectItem>
              <SelectItem value="ai_recipe_created">AI Recipe Created</SelectItem>
              <SelectItem value="ai_menu_created">AI Menu Created</SelectItem>
              <SelectItem value="ai_pantry_item_created">AI Pantry Item Created</SelectItem>
              <SelectItem value="ai_planner_entry_created">AI Planner Entry Created</SelectItem>
              <SelectItem value="ai_selling_price_updated">AI Selling Price Updated</SelectItem>
              <SelectItem value="ai_costing_updated">AI Costing Updated</SelectItem>
              <SelectItem value="ai_shopping_list_updated">AI Shopping List Updated</SelectItem>
              <SelectItem value="ai_recipe_added_to_menu">AI Recipe Added to Menu</SelectItem>
            </SelectContent>
          </Select>

          <Input
            placeholder="Search by email..."
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            className="max-w-xs"
          />

          <Button
            onClick={() => qc.invalidateQueries({ queryKey: ['/api/admin/activity-logs'] })}
            variant="outline"
          >
            Refresh
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Activity Log ({total} total)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Activity</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>Browser</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>IP Address</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log._id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedLog(log)}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell className="font-medium">{log.email}</TableCell>
                      <TableCell>{getActivityBadge(log.activityType, log.success)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{log.platform}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {log.browser?.name || 'N/A'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {log.geolocation ? `${log.geolocation.city}, ${log.geolocation.country}` : 'N/A'}
                      </TableCell>
                      <TableCell className="text-sm font-mono">{log.ipAddress}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={page}
                totalPages={Math.ceil(total / pageSize)}
                onPageChange={setPage}
                totalItems={total}
                itemsPerPage={pageSize}
                onPageSizeChange={setPageSize}
                pageSizeOptions={[5, 10, 20, 50, 100]}
              />
            </>
          )}
        </CardContent>
      </Card>
      {selectedLog && (
        <Sheet open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Activity Detail</SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-3 text-sm">
              {[
                ['ID', selectedLog._id],
                ['Time', new Date(selectedLog.timestamp).toLocaleString()],
                ['User', selectedLog.email],
                ['User ID', selectedLog.userId],
                ['Activity', selectedLog.activityType],
                ['Success', selectedLog.success ? '✅ Yes' : '❌ No'],
                ['Platform', selectedLog.platform],
                ['IP Address', selectedLog.ipAddress],
                ['Browser', selectedLog.browser ? `${selectedLog.browser.name} ${selectedLog.browser.version}` : 'N/A'],
                ['OS', selectedLog.os ? `${selectedLog.os.name} ${selectedLog.os.version}` : 'N/A'],
                ['Device', selectedLog.device ? `${selectedLog.device.type}${selectedLog.device.vendor ? ' · ' + selectedLog.device.vendor : ''}${selectedLog.device.model ? ' ' + selectedLog.device.model : ''}` : 'N/A'],
                ['Location', selectedLog.geolocation ? `${selectedLog.geolocation.city}, ${selectedLog.geolocation.region}, ${selectedLog.geolocation.country}` : 'N/A'],
                ['Timezone', selectedLog.geolocation?.timezone || 'N/A'],
                ['Coordinates', selectedLog.geolocation?.ll ? selectedLog.geolocation.ll.join(', ') : 'N/A'],
                ['Error', selectedLog.errorMessage || 'None'],
                ['User Agent', selectedLog.userAgent || 'N/A'],
              ].map(([label, value]) => (
                <div key={label as string} className="flex gap-2 border-b pb-2">
                  <span className="font-medium w-28 shrink-0 text-muted-foreground">{label}</span>
                  <span className="break-all">{value}</span>
                </div>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
